import React, { useState, useRef } from 'react';
import { usePIM } from '../../context/PIMContext';
import { Modal } from '../ui/Modal';
import { useToast } from '../../context/ToastContext';
import { Input } from '../ui/Input';
import { PrimaryButton } from '../ui/PrimaryButton';
import { Plus, Pencil, Check, X, Download, Upload, Trash } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { parseCSV } from '../../utils/csvHelper';
import { useSortableData } from '../../hooks/useSortableData';
import { SortableHeader } from '../ui/SortableHeader';
import { SearchableSelect } from '../ui/SearchableSelect';

export const PackagingManagementModal = ({ isOpen, onClose, activeCharge }) => {
    const { toast } = useToast();
    const {
        availablePackagingProperties, packagingTypes, packagingGroups,
        addMasterPackagingProperty, deleteMasterPackagingProperty, updateMasterPackagingProperty,
        addPackagingType, deletePackagingType, updatePackagingType,
        addPackagingGroup, deletePackagingGroup, updatePackagingGroup,
        fetchPackagingProperties // Added fetchPackagingProperties
    } = usePIM();

    const [activeTab, setActiveTab] = useState('properties'); // properties, groups, types
    const fileInputRef = useRef(null);

    // --- SORTING ---
    const { items: sortedProperties, requestSort: requestSortProperties, sortConfig: sortConfigProperties } = useSortableData(availablePackagingProperties);
    const { items: sortedGroups, requestSort: requestSortGroups, sortConfig: sortConfigGroups } = useSortableData(packagingGroups);
    const { items: sortedTypes, requestSort: requestSortTypes, sortConfig: sortConfigTypes } = useSortableData(packagingTypes);

    // --- Properties State ---
    const [searchTerm, setSearchTerm] = useState('');
    const [newPropName, setNewPropName] = useState('');
    const [newPropTypeId, setNewPropTypeId] = useState('');
    const [newPropGroupId, setNewPropGroupId] = useState('');
    const [newPropSortOrder, setNewPropSortOrder] = useState(0);
    const [newPropUnit, setNewPropUnit] = useState('');
    const [newPropNotes, setNewPropNotes] = useState('');
    const [newPropIsPublic, setNewPropIsPublic] = useState(1);
    const [editingPropId, setEditingPropId] = useState(null);
    const [editPropData, setEditPropData] = useState({ name: '', type_id: '', group_id: '', sort_order: 0, unit: '', notes: '', is_public: 1 });

    // --- Groups State ---
    const [newGroupName, setNewGroupName] = useState('');
    const [newGroupColor, setNewGroupColor] = useState('#000000');
    const [newGroupSortOrder, setNewGroupSortOrder] = useState(10000);
    const [editingGroupId, setEditingGroupId] = useState(null);
    const [editGroupData, setEditGroupData] = useState({ name: '', color: '', sort_order: 10000 });

    // --- Types State ---
    const [newTypeName, setNewTypeName] = useState('');
    const [editingTypeId, setEditingTypeId] = useState(null);
    const [editTypeName, setEditTypeName] = useState('');

    // --- Handlers: CSV ---
    const handleExport = () => {
        toast.info("Export clicked - DOWNLOAD DISABLED");
    };

    const handleImportClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            const data = await parseCSV(file);
            console.log('Parsed CSV:', data);

            let successCount = 0;
            for (const row of data) {
                const type = packagingTypes.find(t => t.name === row.Type);
                const group = packagingGroups.find(g => g.name === row.Group);

                if (type && group) {
                    await addMasterPackagingProperty({
                        name: row.Name,
                        type_id: type.id,
                        group_id: group.id,
                        sort_order: parseInt(row.SortOrder) || 0,
                        unit: row.Unit,
                        notes: row.Notes || ''
                    });
                    successCount++;
                } else {
                    console.warn(`Skipping row: Type '${row.Type}' or Group '${row.Group}' not found.`);
                }
            }
            if (successCount > 0) {
                toast.success(`Imported ${successCount} properties successfully.`);
                fetchPackagingProperties(activeCharge.id);
            } else {
                toast.error('Import failed. Check console for details.');
            }
        } catch (error) {
            console.error('Import failed:', error);
            toast.error('Import failed. Check console for details.');
        } finally {
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    // --- Handlers: Properties ---
    const handleAddProperty = async () => {
        if (!newPropName || !newPropTypeId || !newPropGroupId) return;
        await addMasterPackagingProperty({
            name: newPropName,
            type_id: newPropTypeId,
            group_id: newPropGroupId,
            sort_order: parseInt(newPropSortOrder) || 0,
            unit: newPropUnit,
            notes: newPropNotes,
            is_public: newPropIsPublic
        });
        setNewPropName('');
        setNewPropSortOrder(0);
        setNewPropUnit('');
        setNewPropNotes('');
        setNewPropIsPublic(1);
    };
    const startEditProperty = (prop) => {
        setEditingPropId(prop.id);
        setEditPropData({
            name: prop.name,
            type_id: prop.type_id,
            group_id: prop.group_id,
            sort_order: prop.sort_order || 0,
            unit: prop.unit || '',
            notes: prop.notes || '',
            is_public: prop.is_public !== undefined && prop.is_public !== null ? prop.is_public : 1
        });
    };
    const saveEditProperty = async (id) => {
        await updateMasterPackagingProperty(id, editPropData);
        setEditingPropId(null);
    };

    // --- Handlers: Groups ---
    const handleAddGroup = async () => {
        if (!newGroupName) return;
        await addPackagingGroup({ name: newGroupName, color: newGroupColor, sort_order: newGroupSortOrder });
        setNewGroupName('');
        setNewGroupColor('#000000');
        setNewGroupSortOrder(10000);
    };
    const startEditGroup = (group) => {
        setEditingGroupId(group.id);
        setEditGroupData({ name: group.name, color: group.color, sort_order: group.sort_order });
    };
    const saveEditGroup = async (id) => {
        await updatePackagingGroup(id, editGroupData);
        setEditingGroupId(null);
    };

    // --- Handlers: Types ---
    const handleAddType = async () => {
        if (!newTypeName) return;
        await addPackagingType({ name: newTypeName });
        setNewTypeName('');
    };
    const startEditType = (type) => {
        setEditingTypeId(type.id);
        setEditTypeName(type.name);
    };
    const saveEditType = async (id) => {
        await updatePackagingType(id, { name: editTypeName });
        setEditingTypeId(null);
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Manage Packaging Definitions" maxWidth="max-w-6xl">
            <div className="flex flex-col h-[80vh]">
                {/* Top Tabs and Actions */}
                <div className="flex justify-between items-center border-b border-stone-700 mb-4">
                    <div className="flex">
                        <button
                            onClick={() => setActiveTab('properties')}
                            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${activeTab === 'properties' ? 'border-blue-500 text-white' : 'border-transparent text-stone-400 hover:text-stone-200'}`}
                        >
                            Properties
                        </button>
                        <button
                            onClick={() => setActiveTab('groups')}
                            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${activeTab === 'groups' ? 'border-blue-500 text-white' : 'border-transparent text-stone-400 hover:text-stone-200'}`}
                        >
                            Groups
                        </button>
                        <button
                            onClick={() => setActiveTab('types')}
                            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${activeTab === 'types' ? 'border-blue-500 text-white' : 'border-transparent text-stone-400 hover:text-stone-200'}`}
                        >
                            Types
                        </button>
                    </div>

                    {/* CSV Actions */}
                    {activeTab === 'properties' && (
                        <div className="flex gap-2 mr-2">
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileChange}
                                className="hidden"
                                accept=".csv"
                            />
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleExport();
                                }}
                                className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded transition-colors border border-green-600 text-green-500 hover:bg-green-900/20"
                            >
                                <Download size={14} /> Export
                            </button>
                            <button
                                onClick={handleImportClick}
                                className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded transition-colors border border-green-600 text-stone-200 hover:bg-green-900/20"
                            >
                                <Upload size={14} /> Import
                            </button>
                        </div>
                    )}
                </div>

                {/* Content Area */}
                <div className="flex-1 flex flex-col min-h-0">

                    {/* --- PROPERTIES TAB --- */}
                    {activeTab === 'properties' && (
                        <div className="flex flex-col h-full">
                            {/* Add Property Form */}
                            <div className="bg-stone-900 p-4 rounded-lg border border-stone-700 mb-4 grid grid-cols-12 gap-3 items-end shrink-0">
                                <div className="col-span-3 space-y-1">
                                    <label className="text-xs font-bold text-stone-500 uppercase">Name</label>
                                    <Input value={newPropName} onChange={(e) => setNewPropName(e.target.value)} placeholder="Property Name" />
                                </div>
                                <div className="col-span-2 space-y-1">
                                    <label className="text-xs font-bold text-stone-500 uppercase">Type</label>
                                    <SearchableSelect
                                        options={packagingTypes.map(t => ({ value: t.id, label: t.name }))}
                                        value={newPropTypeId}
                                        onChange={setNewPropTypeId}
                                        placeholder="Select Type"
                                    />
                                </div>
                                <div className="col-span-2 space-y-1">
                                    <label className="text-xs font-bold text-stone-500 uppercase">Group</label>
                                    <SearchableSelect
                                        options={packagingGroups.map(g => ({ value: g.id, label: g.name }))}
                                        value={newPropGroupId}
                                        onChange={setNewPropGroupId}
                                        placeholder="Select Group"
                                    />
                                </div>
                                <div className="col-span-2 space-y-1">
                                    <label className="text-xs font-bold text-stone-500 uppercase">Sort Order</label>
                                    <Input type="number" value={newPropSortOrder} onChange={(e) => setNewPropSortOrder(e.target.value)} placeholder="0" />
                                </div>
                                <div className="col-span-2 space-y-1">
                                    <label className="text-xs font-bold text-stone-500 uppercase">Unit</label>
                                    <Input value={newPropUnit} onChange={(e) => setNewPropUnit(e.target.value)} placeholder="e.g. mm" />
                                </div>
                                <div className="col-span-3 space-y-1">
                                    <label className="text-xs font-bold text-stone-500 uppercase">Notes</label>
                                    <Input value={newPropNotes} onChange={(e) => setNewPropNotes(e.target.value)} placeholder="Default notes..." />
                                </div>
                                <div className="col-span-1 space-y-1 flex flex-col items-center justify-end pb-2">
                                    <label className="text-[10px] font-bold text-stone-500 uppercase leading-tight mb-1">Public</label>
                                    <input 
                                        type="checkbox" 
                                        checked={newPropIsPublic === 1}
                                        onChange={(e) => setNewPropIsPublic(e.target.checked ? 1 : 0)}
                                        className="w-4 h-4 bg-stone-900 border-stone-600 rounded cursor-pointer"
                                    />
                                </div>
                                <div className="col-span-1">
                                    <PrimaryButton onClick={handleAddProperty} className="w-full justify-center h-[38px]"><Plus size={16} /></PrimaryButton>
                                </div>
                            </div>

                            {/* Property List */}
                            <div className="mb-4 px-1">
                                <Input
                                    placeholder="Search properties..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="bg-stone-950 border-stone-700"
                                />
                            </div>
                            <div className="flex-1 overflow-y-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead className="text-xs text-stone-500 uppercase bg-stone-950 sticky top-0 z-10">
                                        <tr>
                                            <SortableHeader label="Sort" sortKey="sort_order" currentSort={sortConfigProperties} onSort={requestSortProperties} className="w-32 text-center" />
                                            <SortableHeader label="Name" sortKey="name" currentSort={sortConfigProperties} onSort={requestSortProperties} />
                                            <SortableHeader label="Type" sortKey="type_name" currentSort={sortConfigProperties} onSort={requestSortProperties} />
                                            <SortableHeader label="Group" sortKey="group_name" currentSort={sortConfigProperties} onSort={requestSortProperties} />
                                            <SortableHeader label="Unit" sortKey="unit" currentSort={sortConfigProperties} onSort={requestSortProperties} className="w-24" />
                                            <SortableHeader label="Public" sortKey="is_public" currentSort={sortConfigProperties} onSort={requestSortProperties} className="w-20 text-center" />
                                            <SortableHeader label="Notes" sortKey="notes" currentSort={sortConfigProperties} onSort={requestSortProperties} />
                                            <th className="px-3 py-2 border-b border-stone-800 text-right w-24">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="text-sm">
                                        {sortedProperties.filter(p =>
                                            p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                            p.type_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                            p.group_name.toLowerCase().includes(searchTerm.toLowerCase())
                                        ).map(prop => (
                                            <tr key={prop.id} className="border-b border-stone-800 hover:bg-stone-800/20">
                                                {editingPropId === prop.id ? (
                                                    <>
                                                        <td className="px-3 py-2"><Input type="number" max="999999" value={editPropData.sort_order} onChange={(e) => setEditPropData({ ...editPropData, sort_order: e.target.value })} className="h-8 text-xs text-center" /></td>
                                                        <td className="px-3 py-2"><Input value={editPropData.name} onChange={(e) => setEditPropData({ ...editPropData, name: e.target.value })} className="h-8 text-xs" /></td>
                                                        <td className="px-3 py-2">
                                                            <SearchableSelect
                                                                options={packagingTypes.map(t => ({ value: t.id, label: t.name }))}
                                                                value={editPropData.type_id}
                                                                onChange={(val) => setEditPropData({ ...editPropData, type_id: val })}
                                                                placeholder="Type"
                                                                className="w-full"
                                                            />
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <SearchableSelect
                                                                options={packagingGroups.map(g => ({ value: g.id, label: g.name }))}
                                                                value={editPropData.group_id}
                                                                onChange={(val) => setEditPropData({ ...editPropData, group_id: val })}
                                                                placeholder="Group"
                                                                className="w-full"
                                                            />
                                                        </td>
                                                        <td className="px-3 py-2"><Input value={editPropData.unit} onChange={(e) => setEditPropData({ ...editPropData, unit: e.target.value })} className="h-8 text-xs" /></td>
                                                        <td className="px-3 py-2 text-center">
                                                            <input 
                                                                type="checkbox" 
                                                                checked={editPropData.is_public === 1}
                                                                onChange={(e) => setEditPropData({ ...editPropData, is_public: e.target.checked ? 1 : 0 })}
                                                                className="w-4 h-4 rounded border-stone-600 bg-stone-900 cursor-pointer"
                                                            />
                                                        </td>
                                                        <td className="px-3 py-2"><Input value={editPropData.notes} onChange={(e) => setEditPropData({ ...editPropData, notes: e.target.value })} className="h-8 text-xs" /></td>
                                                        <td className="px-3 py-2 text-right">
                                                            <div className="flex justify-end gap-1">
                                                                <button onClick={() => saveEditProperty(prop.id)} className="text-green-500 p-1 hover:bg-stone-800 rounded"><Check size={14} /></button>
                                                                <button onClick={() => setEditingPropId(null)} className="text-stone-500 p-1 hover:bg-stone-800 rounded"><X size={14} /></button>
                                                            </div>
                                                        </td>
                                                    </>
                                                ) : (
                                                    <>
                                                        <td className="px-3 py-2 text-stone-500 text-center">{prop.sort_order}</td>
                                                        <td className="px-3 py-2 text-stone-300">{prop.name}</td>
                                                        <td className="px-3 py-2 text-stone-400">{prop.type_name}</td>
                                                        <td className="px-3 py-2"><Badge color={prop.group_color}>{prop.group_name}</Badge></td>
                                                        <td className="px-3 py-2 text-stone-400">{prop.unit}</td>
                                                        <td className="px-3 py-2 text-center">
                                                            {prop.is_public === 1 
                                                                ? <span className="text-green-500"><Check size={16} className="mx-auto" /></span>
                                                                : <span className="text-stone-600"><X size={16} className="mx-auto" /></span>}
                                                        </td>
                                                        <td className="px-3 py-2 text-stone-400 truncate max-w-[150px]">{prop.notes}</td>
                                                        <td className="px-3 py-2 text-right">
                                                            <div className="flex justify-end gap-1">
                                                                <button onClick={() => startEditProperty(prop)} className="text-stone-500 hover:text-blue-400 p-1 hover:bg-stone-800 rounded"><Pencil size={14} /></button>
                                                                <button onClick={() => deleteMasterPackagingProperty(prop.id)} className="text-stone-500 hover:text-red-400 p-1 hover:bg-stone-800 rounded"><Trash size={14} /></button>
                                                            </div>
                                                        </td>
                                                    </>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* --- GROUPS TAB --- */}
                    {activeTab === 'groups' && (
                        <div className="flex flex-col h-full">
                            <div className="bg-stone-900 p-4 rounded-lg border border-stone-700 mb-4 grid grid-cols-12 gap-3 items-end shrink-0">
                                <div className="col-span-4 space-y-1">
                                    <label className="text-xs font-bold text-stone-500 uppercase">Group Name</label>
                                    <Input value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)} placeholder="e.g. Dimensions" />
                                </div>
                                <div className="col-span-2 space-y-1">
                                    <label className="text-xs font-bold text-stone-500 uppercase">Sort Order</label>
                                    <Input type="number" value={newGroupSortOrder} onChange={(e) => setNewGroupSortOrder(e.target.value)} placeholder="10000" />
                                </div>
                                <div className="col-span-2 space-y-1">
                                    <label className="text-xs font-bold text-stone-500 uppercase">Color</label>
                                    <Input type="color" value={newGroupColor} onChange={(e) => setNewGroupColor(e.target.value)} className="h-[38px] p-1" />
                                </div>
                                <div className="col-span-2">
                                    <PrimaryButton onClick={handleAddGroup} className="w-full justify-center h-[38px]"><Plus size={16} /> Add</PrimaryButton>
                                </div>
                            </div>

                            <div className="flex-1 overflow-y-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead className="text-xs text-stone-500 uppercase bg-stone-950 sticky top-0 z-10">
                                        <tr>
                                            <SortableHeader label="Sort" sortKey="sort_order" currentSort={sortConfigGroups} onSort={requestSortGroups} className="w-32 text-center" />
                                            <SortableHeader label="Color" sortKey="color" currentSort={sortConfigGroups} onSort={requestSortGroups} className="w-16 text-center" />
                                            <SortableHeader label="Group Name" sortKey="name" currentSort={sortConfigGroups} onSort={requestSortGroups} />
                                            <th className="px-3 py-2 border-b border-stone-800 text-right w-24">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="text-sm">
                                        {sortedGroups.map(group => (
                                            <tr key={group.id} className="border-b border-stone-800 hover:bg-stone-800/20">
                                                {editingGroupId === group.id ? (
                                                    <>
                                                        <td className="px-3 py-2">
                                                            <Input
                                                                type="number"
                                                                value={editGroupData.sort_order}
                                                                onChange={(e) => setEditGroupData({ ...editGroupData, sort_order: e.target.value })}
                                                                className="h-8 text-xs text-center"
                                                                placeholder="Sort"
                                                            />
                                                        </td>
                                                        <td className="px-3 py-2 text-center">
                                                            <Input
                                                                type="color"
                                                                value={editGroupData.color}
                                                                onChange={(e) => setEditGroupData({ ...editGroupData, color: e.target.value })}
                                                                className="w-8 h-8 p-0 border-none inline-block cursor-pointer"
                                                            />
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <Input
                                                                value={editGroupData.name}
                                                                onChange={(e) => setEditGroupData({ ...editGroupData, name: e.target.value })}
                                                                className="h-8 text-xs w-full"
                                                            />
                                                        </td>
                                                        <td className="px-3 py-2 text-right">
                                                            <div className="flex justify-end gap-1">
                                                                <button onClick={() => saveEditGroup(group.id)} className="text-green-500 p-1 hover:bg-stone-800 rounded"><Check size={14} /></button>
                                                                <button onClick={() => setEditingGroupId(null)} className="text-stone-500 p-1 hover:bg-stone-800 rounded"><X size={14} /></button>
                                                            </div>
                                                        </td>
                                                    </>
                                                ) : (
                                                    <>
                                                        <td className="px-3 py-2 text-stone-500 text-center">{group.sort_order}</td>
                                                        <td className="px-3 py-2 text-center">
                                                            <div className="w-4 h-4 rounded-full inline-block" style={{ backgroundColor: group.color }}></div>
                                                        </td>
                                                        <td className="px-3 py-2 font-medium text-stone-200">{group.name}</td>
                                                        <td className="px-3 py-2 text-right">
                                                            <div className="flex justify-end gap-1">
                                                                <button onClick={() => startEditGroup(group)} className="text-stone-500 hover:text-blue-400 p-1 hover:bg-stone-800 rounded"><Pencil size={14} /></button>
                                                                <button onClick={() => deletePackagingGroup(group.id)} className="text-stone-500 hover:text-red-400 p-1 hover:bg-stone-800 rounded"><Trash size={14} /></button>
                                                            </div>
                                                        </td>
                                                    </>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* --- TYPES TAB --- */}
                    {activeTab === 'types' && (
                        <div className="flex flex-col h-full">
                            <div className="bg-stone-900 p-4 rounded-lg border border-stone-700 mb-4 flex gap-4 items-end shrink-0">
                                <div className="flex-1 space-y-1">
                                    <label className="text-xs font-bold text-stone-500 uppercase">Type Name</label>
                                    <Input value={newTypeName} onChange={(e) => setNewTypeName(e.target.value)} placeholder="e.g. Text, Number" />
                                </div>
                                <PrimaryButton onClick={handleAddType} className="h-[38px] px-4"><Plus size={16} /> Add</PrimaryButton>
                            </div>

                            <div className="flex-1 overflow-y-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead className="text-xs text-stone-500 uppercase bg-stone-950 sticky top-0 z-10">
                                        <tr>
                                            <SortableHeader label="Type Name" sortKey="name" currentSort={sortConfigTypes} onSort={requestSortTypes} />
                                            <th className="px-3 py-2 border-b border-stone-800 text-right w-24">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="text-sm">
                                        {sortedTypes.map((type) => (
                                            <tr key={type.id} className="border-b border-stone-800 hover:bg-stone-800/20">
                                                {editingTypeId === type.id ? (
                                                    <>
                                                        <td className="px-3 py-2">
                                                            <Input
                                                                value={editTypeName}
                                                                onChange={(e) => setEditTypeName(e.target.value)}
                                                                className="h-8 text-xs w-full"
                                                            />
                                                        </td>
                                                        <td className="px-3 py-2 text-right">
                                                            <div className="flex justify-end gap-1">
                                                                <button onClick={() => saveEditType(type.id)} className="text-green-500 p-1 hover:bg-stone-800 rounded"><Check size={14} /></button>
                                                                <button onClick={() => setEditingTypeId(null)} className="text-stone-500 p-1 hover:bg-stone-800 rounded"><X size={14} /></button>
                                                            </div>
                                                        </td>
                                                    </>
                                                ) : (
                                                    <>
                                                        <td className="px-3 py-2 font-medium text-stone-200">{type.name}</td>
                                                        <td className="px-3 py-2 text-right">
                                                            <div className="flex justify-end gap-1">
                                                                <button onClick={() => startEditType(type)} className="text-stone-500 hover:text-blue-400 p-1 hover:bg-stone-800 rounded"><Pencil size={14} /></button>
                                                                <button onClick={() => deletePackagingType(type.id)} className="text-stone-500 hover:text-red-400 p-1 hover:bg-stone-800 rounded"><Trash size={14} /></button>
                                                            </div>
                                                        </td>
                                                    </>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
};
